import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createUser, createAdminUser } from "../../services/userService";
import SuccessMessageModal from "../Reusable/SuccessMessageModal";
import WarningMessageModal from "../Reusable/WarningMessageModal";
import { assignUserToApartment, getApartmentById } from "../../services/apartmentService";
import { assignPlaceToUser } from "../../services/userPlaceService";

const CreateApartmentUserModal = ({ isOpen, onClose, apartmentId }) => {
  const ORGANIZATION = JSON.parse(sessionStorage.getItem("organization"));
  const [isAdmin, setIsAdmin] = useState(false);
  const [userData, setUserData] = useState({
    username: "",
    password: "",
    email: "",
    first_name: "",
    second_name: "",
    first_last_name: "",
    second_last_name: "",
    documento: "",
    photo: "",
    organizacion_id: ORGANIZATION.organizacion_id,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [warningMessageVisible, setWarningMessageVisible] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleCheckboxChange = () => {
    setIsAdmin(!isAdmin);
    if (isAdmin) {
      setUserData({
        username: userData.username,
        password: userData.password,
        email: "",
        first_name: "",
        second_name: "",
        first_last_name: "",
        second_last_name: "",
        documento: "",
        photo: "",
        organizacion_id: ORGANIZATION.organizacion_id,
      });
    }
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const toggleShowPassword = () => setShowPassword((prev) => !prev);
  const toggleShowConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

  const passwordIsValid = () =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/.test(userData.password);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };


  const handleSave = async (e) => {
    e.preventDefault();
    let response;

    if (!userData.username || !userData.password || !confirmPassword) {
      setErrorMessage("El nombre de usuario y ambas contraseñas son obligatorios.");
      setWarningMessageVisible(true);
      return;
    }
    if (userData.password !== confirmPassword) {
      setErrorMessage("Las contraseñas no coinciden.");
      setWarningMessageVisible(true);
      return;
    }
    if (!passwordIsValid()) {
      setErrorMessage("La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un número.");
      setWarningMessageVisible(true);
      return;
    }
    if (isAdmin && (!userData.email || !userData.first_name || !userData.first_last_name || !userData.documento || !userData.photo)) {
      setErrorMessage("Todos los campos de administrador son obligatorios, incluyendo la foto.");
      setWarningMessageVisible(true);
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    const performSave = async () => {
      try {
        // 1. Crear el usuario
        let userResponse;
        if (isAdmin) {
          // Los admins se crean con la organización directamente
          userResponse = await createAdminUser(userData);
        } else {
          const { username, password, email } = userData;
          userResponse = await createUser({ username, password, email });
        }
        const newUserId = userResponse.data.user.id;

        // 2. Obtener el place_id de la sede a la que pertenece el apartamento
        const apartmentDetails = await getApartmentById(apartmentId);
        const placeId = apartmentDetails.place_id;

        if (!placeId) {
          throw new Error("No se pudo obtener la sede del apartamento para la asignación.");
        }

        // 3. Asignar el usuario a la sede (esto debería corregir la organización)
        await assignPlaceToUser({ user_id: newUserId, place_id: placeId });

        // 4. Asignar el usuario al apartamento
        await assignUserToApartment(apartmentId, newUserId);
        
        // Si todo tiene éxito
        setIsLoading(false);
        setSuccessMessageVisible(true);

      } catch (error) {
        console.error("Error during save process:", error);
        const detail = error.response?.data?.detail || "Ocurrió un error en el proceso de creación y asignación del usuario.";
        setErrorMessage(detail);
        setWarningMessageVisible(true);
        setIsLoading(false);
      }
    };

    performSave();
  };
  
  const handleSuccessClose = () => {
    setSuccessMessageVisible(false);
    window.location.reload();
  };

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>Crear Nuevo Usuario para Apartamento</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center"><CircularProgress /></Box>
        ) : (
          <Box component="form">
            <FormControlLabel
              control={<Checkbox checked={isAdmin} onChange={handleCheckboxChange} color="primary" />}
              label="Crear Usuario Administrador"
            />
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField label="Nombre de Usuario *" name="username" value={userData.username} onChange={handleChange} fullWidth /></Grid>
              <Grid item xs={12}><TextField label="Correo Electrónico *" name="email" value={userData.email} type="email" onChange={handleChange} fullWidth /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nueva Contraseña *"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={userData.password}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={toggleShowPassword}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>)}}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirmar Contraseña *"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  fullWidth
                  InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton onClick={toggleShowConfirmPassword}>{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>)}}
                />
              </Grid>
              {isAdmin && (
                <>
                  <Grid item xs={12} sm={6}><TextField label="Primer Nombre *" name="first_name" value={userData.first_name} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} sm={6}><TextField label="Segundo Nombre" name="second_name" value={userData.second_name} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} sm={6}><TextField label="Primer Apellido *" name="first_last_name" value={userData.first_last_name} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} sm={6}><TextField label="Segundo Apellido" name="second_last_name" value={userData.second_last_name} onChange={handleChange} fullWidth /></Grid>
                  <Grid item xs={12} sm={6}><TextField label="Documento de Identificación *" name="documento" value={userData.documento} onChange={handleChange} fullWidth /></Grid>
                </>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} style={{ color: "#1d1d1b" }}>Cancelar</Button>
        <Button onClick={handleSave} style={{ backgroundColor: "#0A2139", color: "#f1f1f1" }} disabled={isLoading} variant="contained">Guardar</Button>
      </DialogActions>
      <SuccessMessageModal message="Usuario registrado y asignado exitosamente." isOpen={successMessageVisible} onClose={handleSuccessClose} />
      <WarningMessageModal message={errorMessage} isOpen={warningMessageVisible} onClose={() => setWarningMessageVisible(false)} />
    </Dialog>
  );
};

export default CreateApartmentUserModal;
